import ArrowRight from '@assets/icons/arrow-right.svg';
import ChevronLeft from '@assets/icons/chevron-left.svg';
import ChevronRight from '@assets/icons/chevron-right.svg';
import React from 'react';
import { Button, Props as ButtonProps } from './button';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, SCREEN_PADDING_LARGE, shadow, TYPOGRAPHY } from '@/styles';
import {
  StyleProp,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { useSmallLayoutSizeValue } from '@/contexts/layout-context';

const BUTTON_HIT_SLOP = {
  bottom: 0,
  left: 8,
  right: 8,
  top: 0,
};

interface Props {
  ImageComponent?: React.ReactNode;
  itemCount?: number;
  largeHeaderStyle?: StyleProp<ViewStyle>;
  onLeftArrowPress?: () => void;
  onRightArrowPress?: () => void;
  onSeeAllPress?: () => void;
  showAllThreshold?: number;
  TagComponent?: React.ReactNode;
  title: string;
}

const ARROW_BUTTON_DIMENSION = 40;

export const HorizontalListHeader: React.FC<Props> = ({
  ImageComponent,
  itemCount = 0,
  largeHeaderStyle,
  onSeeAllPress,
  showAllThreshold = 2,
  onRightArrowPress,
  onLeftArrowPress,
  TagComponent,
  title,
}) => {
  const [styles, theme] = useStyles();

  const headerStyle = useSmallLayoutSizeValue<StyleProp<ViewStyle>>(
    styles.header,
    [styles.header, styles.headerLarge, largeHeaderStyle],
  );

  const isButtonDisabled = showAllThreshold > itemCount || !onSeeAllPress;

  const sharedArrowButtonProps: Partial<ButtonProps> = {
    iconProps: {
      height: 16,
      width: 7,
    },
    rounded: true,
    size: 'small',
    style: styles.arrowButton,
    type: 'secondary',
  };

  return (
    <View
      style={[headerStyle, ImageComponent ? styles.headerWithImage : null]}
      testID="horizontal-list-header"
    >
      <TouchableOpacity
        disabled={isButtonDisabled}
        hitSlop={BUTTON_HIT_SLOP}
        onPress={onSeeAllPress}
        style={styles.touchable}
      >
        {ImageComponent ? (
          <View style={styles.imageWrapper}>{ImageComponent}</View>
        ) : null}
        <Text numberOfLines={1} style={[TYPOGRAPHY.large, FONTS.primaryBold]}>
          {title}
        </Text>
        {isButtonDisabled ? null : (
          <View style={styles.titleRightArrowContainer}>
            <ArrowRight
              fill={theme.colors.primary}
              height={12}
              testID="right-arrow-icon"
              width={12}
            />
          </View>
        )}
      </TouchableOpacity>
      {TagComponent}
      {onRightArrowPress && onLeftArrowPress ? (
        <View style={styles.arrowWrapper}>
          <Button
            {...sharedArrowButtonProps}
            Icon={ChevronLeft}
            onPress={onLeftArrowPress}
            testID="horizontal-header-left-arrow"
            trackingData={null}
          />
          <Button
            {...sharedArrowButtonProps}
            Icon={ChevronRight}
            onPress={onRightArrowPress}
            testID="horizontal-header-right-arrow"
            trackingData={null}
          />
        </View>
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  arrowButton: {
    backgroundColor: theme.colors.white,
    height: ARROW_BUTTON_DIMENSION,
    width: ARROW_BUTTON_DIMENSION,
    ...shadow({
      color: theme.colors.black,
      elevation: 5,
      opacity: 0.1,
      radius: 4,
      x: 0,
      y: 0,
    }),
  },
  arrowWrapper: {
    flexDirection: 'row',
    gap: 16,
    marginLeft: 16,
  },
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
    marginHorizontal: 16,
  },
  headerLarge: {
    marginHorizontal: SCREEN_PADDING_LARGE,
  },
  headerWithImage: {
    marginBottom: 0,
  },
  imageWrapper: {
    marginRight: 16,
  },
  titleRightArrowContainer: {
    marginLeft: 8,
  },
  touchable: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    paddingVertical: 8,
  },
}));
